import { Recurrence } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePaneProps, SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import * as _ from 'underscore';
import { FeatureFlags } from '../FeatureFlags';
import { IRemoteRefreshScheduleTypeNameFilter } from '../filters/RemoteRefreshScheduleTypeNameFilter';
import { IActionContext } from '../helpers/ActionContext';
import { RemoteRefreshScheduleEditorViewModel } from '../helpers/CronExpressionHelper';
import { L10n } from '../L10n/L10n';
import { IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { IRemoteRefreshScheduleInfo } from '../models/IRemoteRefreshScheduleInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Module } from '../Module';
import { RemoteRefreshSchedules } from '../resources/RemoteRefreshSchedules';
import { RemoteRefreshTypes } from '../ServerApi/requests/GetRemoteRefreshSchedulesRequest';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { ISelfServiceScheduleDetails } from '../ServerApi/types/ISchedule';
import { ChangeScheduleAction, IChangeSelfServiceScheduleScope } from './ChangeScheduleAction';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ContentActionNotification } from './ContentActionNotification';
import { ModalService } from './ModalService';

export interface ICreateRemoteRefreshScheduleScope extends ng.IScope {
  availableTypeOptions: string[];
  prompt: string;
  scheduleInfo: IRemoteRefreshScheduleInfo;
  startOnMonday: boolean;
  editorViewModel: RemoteRefreshScheduleEditorViewModel;
  minuteIncrementOptions: number[];
}

interface IScheduleInfo {
  refreshType: string;
}

export interface ICreateRemoteRefreshSelfServiceScheduleScope extends IChangeSelfServiceScheduleScope {
  availableTypeOptions: string[];
  scheduleInfo: IScheduleInfo;
}

export function setupDefaultScheduleDetailsForSSSWidget<T extends ICreateRemoteRefreshSelfServiceScheduleScope>(scope: T,
  availableTypes: string[], changeScheduleAction: ChangeScheduleAction, remoteAgentTimeZone: string) {
  scope.selfServiceSchedulePaneProps = getDefaultSelfServiceSchedulePanePropsForSSSWidget(changeScheduleAction, remoteAgentTimeZone);
  scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback = (newRecurrence: Recurrence) => {
    scope.$apply(() => {
      scope.selfServiceSchedulePaneProps = {
        recurrenceModel: newRecurrence,
        timeZoneDetails: scope.selfServiceSchedulePaneProps.timeZoneDetails,
        recurrenceModelChangedCallback: scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback,
        type: scope.selfServiceSchedulePaneProps.type,
      };
    });
  };
  scope.availableTypeOptions = availableTypes;
  scope.scheduleInfo = {
    refreshType: RemoteRefreshTypes.Full,
  };
}

export function getDefaultSelfServiceSchedulePanePropsForSSSWidget(
  changeScheduleAction: ChangeScheduleAction, remoteAgentTimeZone: string): SelfServiceSchedulePaneProps {
    const scheduleDetails: ISelfServiceScheduleDetails = {
      startTime: 300,
      endTime: 0,
      recurrenceMinutes: 1440,
      recurrenceDaysOfWeek: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      recurrenceDayOfMonth: '0',
    };

    const selfServiceSchedulePaneProps = changeScheduleAction.getSelfServiceSchedulePaneProps(
      scheduleDetails, remoteAgentTimeZone != null, remoteAgentTimeZone, 'Weekly',
    SelfServiceSchedulePaneType.BridgeExtractRefresh);
    return selfServiceSchedulePaneProps;
}

export class CreateRemoteRefreshScheduleAction {

  public static $inject = [
    '$translate',
    '$filter',
    'ConfirmActionDialog',
    'ContentActionNotification',
    'RemoteRefreshSchedules'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $filter: ng.IFilterService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private ContentActionNotification: ContentActionNotification,
    private RemoteRefreshSchedules: RemoteRefreshSchedules) { }

  public execute(context: IActionContext,
    item: IDataSourceDetailInfo,
    availableTypes: string[],
    changeScheduleAction: ChangeScheduleAction): ng.IPromise<IResult> {
    if (FeatureFlags.remoteRefreshSelfServiceScheduleWidgetEnabled) {
      return this.openDialogWithSSSWidget(context, item, availableTypes, changeScheduleAction);
    }

    return this.openDialogWithOldWidget(context, item, availableTypes);
  }

  private openDialogWithOldWidget(context: IActionContext,
    item: INamedItemInfo,
    availableTypes: string[]): ng.IPromise<IResult> {

    const scope = <ICreateRemoteRefreshScheduleScope>context.scope.$new();
    scope.availableTypeOptions = availableTypes;
    scope.minuteIncrementOptions = RemoteRefreshScheduleEditorViewModel.minuteIncrementOptions;
    scope.prompt = this.$translate.instant('createRemoteRefreshScheduleCustomDialogPrompt', { ITEM_NAME: L10n.elideIfLong(item.name) });
    scope.startOnMonday = L10n.firstDayOfWeekIsMonday();
    scope.editorViewModel = new RemoteRefreshScheduleEditorViewModel();
    scope.scheduleInfo = {
      id: '',
      contentType: 'remoteRefreshSchedule',
      definition: scope.editorViewModel.getCronExpression(),
      refreshType: RemoteRefreshTypes.Full,
      runNextAt: null
    };

    const action = () => {
      scope.scheduleInfo.definition = scope.editorViewModel.getCronExpression();
      return this.RemoteRefreshSchedules.create(item.id, scope.scheduleInfo);
    };
    const confirmDisabled = () => !scope.editorViewModel.isValid();
    return this.openDialog(context, 'confirmActionDialogRemoteRefreshSchedule.html',
      action, confirmDisabled, scope, () => scope.scheduleInfo.refreshType);
  }

  private openDialogWithSSSWidget(context: IActionContext,
    datasourceDetailInfo: IDataSourceDetailInfo,
    availableTypes: string[],
    changeScheduleAction: ChangeScheduleAction): ng.IPromise<IResult> {
    const scope = <ICreateRemoteRefreshSelfServiceScheduleScope>context.scope.$new();
    setupDefaultScheduleDetailsForSSSWidget(scope, availableTypes, changeScheduleAction, datasourceDetailInfo.remoteAgentTimeZone);

    const action = () => {
      const scheduleInfo: IRemoteRefreshScheduleInfo = {
        id: '',
        contentType: 'remoteRefreshSchedule',
        definition: '',
        refreshType: scope.scheduleInfo.refreshType,
        scheduleDetails: scope.selfServiceSchedulePaneProps.recurrenceModel.recurrenceDetails,
        scheduleType: scope.selfServiceSchedulePaneProps.recurrenceModel.recurrenceType
      };
      return this.RemoteRefreshSchedules.create(datasourceDetailInfo.id, scheduleInfo);
    };
    const confirmDisabled = () => false;
    return this.openDialog(context, 'confirmActionDialogRemoteRefreshSelfServiceSchedule.html',
      action, confirmDisabled, scope, () => scope.scheduleInfo.refreshType);
  }

  private openDialog(context: IActionContext,
    bodyTemplateUrl: string,
    action: (confirmActionDialogInstance: IConfirmActionDialogInstance) => any,
    confirmDisabled: () => boolean,
    scope: ng.IScope,
    getRefreshType: () => string): ng.IPromise<IResult> {
    const dialogInstance = this.ConfirmActionDialog.open({
      title: this.$translate.instant('createRemoteRefreshScheduleDialogTitle'),
      bodyTemplateUrl: bodyTemplateUrl,
      size: '500px',
      scope: scope,
      position: _.extend(ModalService.anchoredTo(context.targetElement), { reserveHeight: 600 }),
      actionName: this.$translate.instant('createRemoteRefreshScheduleDialogSubmit'),
      action: action,
      confirmDisabled: confirmDisabled,
    });
    return dialogInstance.result
      .then((result: IResult) => {
        if (result.errors && result.errors.length) {
          const errorMessage = this.errorMessageFor(result.errors[0]);
          dialogInstance.showErrorMessage(errorMessage);
        } else {
          this.ContentActionNotification.notifySuccess(
            this.$translate.instant('createRemoteRefreshScheduleSuccess',
              { REFRESH_TYPE_NAME: this.$filter<IRemoteRefreshScheduleTypeNameFilter>('remoteRefreshScheduleTypeName')(getRefreshType()) })
          );
        }
        return result;
      })
      .finally(() => scope.$destroy());
  }

  private errorMessageFor(error: IError) {
    return this.$translate.instant('errorUnknown');
  }

}

Module.instance().service('CreateRemoteRefreshScheduleAction', CreateRemoteRefreshScheduleAction);
